import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    LineChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Line,
    ResponsiveContainer,
    // BarChart,
    // Bar,
    // Text,
} from "recharts";

import { CalculatorContext } from "context/Calculator";
import { fetchCalculationResult } from "firebaseApp";
import { labelForBackendLocationKey } from "utils/canadaWeatherLocations";
import { API_URL, GAS_CONVERSION_FACTOR } from "utils/constants";

// import { capitalizeFirstLetter } from "utils/helpers";

import PageLayout from "components/PageLayout";
import Input from "components/Input";
import Button from "components/Button";
import CustomToolTip from "components/CustomToolTip";
import { default as TextToolTip } from "components/Tooltip";

import "./style.scss";

const CHART_X_MAX = 20;

/** Lower X bound for the results chart; mirrors backend x_plot when tbpt is missing. */
function getChartXMin(dtemp, tbpt) {
    const designTemp = typeof dtemp === "number" && !Number.isNaN(dtemp) ? dtemp : -25;
    const hasTbpt = tbpt != null && !Number.isNaN(tbpt);

    if (hasTbpt) {
        return Math.min(designTemp, -25, Math.round(tbpt - 2));
    }

    return designTemp < -25 ? designTemp : -25;
}

function getChartXTicks(xMin, xMax = CHART_X_MAX) {
    const count = Math.floor((xMax - xMin) / 5) + 1;
    return Array.from({ length: Math.max(count, 1) }, (_, i) => xMin + i * 5);
}

function renderGasVolume(m3Value, units, { decimals } = {}) {
    const m3 = Number(m3Value);
    if (Number.isNaN(m3)) return "N/A";

    if (units === "GJ") {
        const gj = m3 / GAS_CONVERSION_FACTOR;
        const formatted = decimals != null ? gj.toFixed(decimals) : gj.toFixed(2);
        return `${formatted}`;
        // return `${formatted} GJ`;
    }

    const formatted = decimals != null ? m3.toFixed(decimals) : m3;
    return (
        <>
            {formatted}
            {/* {formatted} m<sup className="custom-sup">3</sup> */}
        </>
    );
}

function GasUnitToggle({ value, onChange }) {
    return (
        <select
            className="result-row__unit-select"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Gas volume units"
        >
            <option value="m3">m³</option>
            <option value="GJ">GJ</option>
        </select>
    );
}

function ResultRow({ label, tooltipText, value, unitToggle, indented = false, fullWidth = false }) {
    if (fullWidth) {
        return <div className="result-row result-row--full">{label}</div>;
    }

    return (
        <div className={`result-row${indented ? " result-row--indented" : ""}`}>
            <div className="result-row__label">
                {!indented &&
                    (tooltipText ? (
                        <TextToolTip tooltipText={tooltipText}>
                            <span>{label}</span>
                        </TextToolTip>
                    ) : (
                        <span>{label}</span>
                    ))}
            </div>
            <div className="result-row__value">
                <div className="result-row__value-content">
                    <b className="result-value">{value}</b>
                    {unitToggle}
                </div>
            </div>
        </div>
    );
}

function normalizeGasUnit(value) {
    return value === "GJ" ? "GJ" : "m3";
}

const Result = () => {
    const { id } = useParams();
    const { calculationResults, setCalculationResults } = useContext(CalculatorContext);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [projectName, setProjectName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [wantsEmailUpdates, setWantsEmailUpdates] = useState(false);
    const [isEmailSending, setIsEmailSending] = useState(false);
    const [emailFeedback, setEmailFeedback] = useState(null);
    const [gasuseUnits, setGasuseUnits] = useState(() =>
        normalizeGasUnit(calculationResults?.gasuse_total_units),
    );
    const [dhwUnits, setDhwUnits] = useState(() =>
        normalizeGasUnit(calculationResults?.dhw_monthly_units),
    );

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    // Units come from the API response / Firestore-saved result.
    useEffect(() => {
        const gas = calculationResults?.gasuse_total_units;
        const dhw = calculationResults?.dhw_monthly_units;
        if (gas === "GJ" || gas === "m3") {
            setGasuseUnits(gas);
        } else if (calculationResults?.result_id) {
            setGasuseUnits("m3");
        }
        if (dhw === "GJ" || dhw === "m3") {
            setDhwUnits(dhw);
        } else if (calculationResults?.result_id) {
            setDhwUnits("m3");
        }
    }, [
        calculationResults?.result_id,
        calculationResults?.gasuse_total_units,
        calculationResults?.dhw_monthly_units,
    ]);

    const handleEmailResults = async () => {
        const trimmedProjectName = projectName.trim();
        const trimmedEmail = userEmail.trim();

        if (!trimmedProjectName || !trimmedEmail || !id) {
            setEmailFeedback({
                type: "error",
                message: "Please enter a project name and email address.",
            });
            return;
        }

        setIsEmailSending(true);
        setEmailFeedback(null);

        try {
            await axios.post(`${API_URL}/email_results`, {
                project_name: trimmedProjectName,
                user_email: trimmedEmail,
                result_id: id,
                subscribe_updates: wantsEmailUpdates,
            });
            setEmailFeedback({
                type: "success",
                message: "Results sent. Check your inbox shortly.",
            });
        } catch (error) {
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                "Could not send results. Please try again.";
            setEmailFeedback({ type: "error", message });
        } finally {
            setIsEmailSending(false);
        }
    };

    useEffect(() => {
        if (!id) return;

        const cachedId = calculationResults?.result_id;
        if (cachedId != null && String(cachedId) === String(id)) {
            return;
        }

        let cancelled = false;

        const fetchResult = async () => {
            setIsLoading(true);
            setFetchError(null);
            try {
                const data = await fetchCalculationResult(id);
                if (!cancelled) {
                    setCalculationResults(data);
                }
            } catch (error) {
                if (!cancelled) {
                    const message =
                        error.code === "not-found" || error.code === "invalid-data"
                            ? error.message
                            : error.code === "permission-denied"
                              ? "You do not have permission to view these results."
                              : error?.message || "Could not load results. Please check the link and try again.";
                    setFetchError(message);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        fetchResult();

        return () => {
            cancelled = true;
        };
    }, [id, calculationResults?.result_id, setCalculationResults]);

    if (isLoading) {
        return (
            <PageLayout pageTitle={"ThermalPoint Results"}>
                <p>Loading results…</p>
            </PageLayout>
        );
    }

    if (fetchError) {
        return (
            <PageLayout pageTitle={"ThermalPoint Results"}>
                <p>{fetchError}</p>
            </PageLayout>
        );
    }

    if (!calculationResults?.result_id) {
        return (
            <PageLayout pageTitle={"ThermalPoint Results"}>
                <p>No results found.</p>
            </PageLayout>
        );
    }

    // "gasuse_total": 2500.0,
    // "dhw_monthly": 20.0,
    // "balance_point": 16.0,
    // "furnace_eff": 95.0,
    // "derate": 20.0,
    // "location": "toronto",
    // "result_heat_loss": 47,
    // "result_temp": -18

    const {
        project_name,
        dateMode,
        year,
        // gasuse_annual,
        // start,
        // end,
        // gasuse_custom,
        dhw_monthly,
        // dhw_annual,
        // dhw_total,
        gasuse_total,
        gasuse_heat,
        // balance_point_adj,
        furnace_eff,
        derate,
        location,

        home_type,
        home_age,
        indoor_temp_heat,
        energy_upgrades,
        normalized_location_name,
        dtemp,
        heatpumpcap_5,
        heatpumpcap_17,
        heatpumpcap_47,
        heatloss_design,
        // result_temp,
        line_graph_data,
        // bar_data_graph,
        tbpt,
        frac_above_tbpt,
        gas_period_start,
        gas_period_end,
    } = calculationResults;

    const formatLocation = (loc) => {
        if (!loc) return "";
        if (!loc.includes("_")) {
            return loc.charAt(0).toUpperCase() + loc.slice(1).toLowerCase();
        }
        return loc
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    const trimmedNorm = typeof normalized_location_name === "string" ? normalized_location_name.trim() : "";
    const displayLocation =
        trimmedNorm || labelForBackendLocationKey(location) || (location ? formatLocation(location) : "N/A");

    const formatYmd = (ymd) => {
        if (!ymd || typeof ymd !== "string") return "";
        const [y, m, d] = ymd.split("-").map(Number);
        if (!y || !m || !d) return ymd;
        return new Date(y, m - 1, d).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const capitalizeFirstLetter = (str) => {
        if (typeof str !== "string") return str;
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const chartXMin = getChartXMin(dtemp, tbpt);
    const chartXTicks = getChartXTicks(chartXMin);

    return (
        <PageLayout
            pageTitle={project_name ? `ThermalPoint Results for ${project_name}` : "ThermalPoint Results"}
            pageSubtitle={<div style={{ fontSize: "12px" }}></div>}
        >
            {/* <h5 className="result-title">Results</h5> */}
            <div className="results-summary">
                <div className="row">
                    <div className="left-col" align="left">
                        Estimated average heat loss at outdoor design temperature:
                    </div>
                    <div className="right-col">
                        <TextToolTip
                            tooltipText="This parameter is not the design heat loss (DHL) calculated per CSA F280-12. Instead, this parameter 
            is an empirical estimate of the home's average heat loss at the outdoor design temperature, which is then multiplied by a safety margin. 
            This parameter may be lower than the DHL.
            Similarly, the heat loss curve presented below represents average heat loss (multiplied by the safety margin). 
            The DHL is required to size backup and supplemental heating systems and it is the contractor or mechanical 
            designer's responsibility to ensure adequate sizing. Please refer to the terms and conditions for the 
            limitations of this tool for sizing backup or supplemental systems."
                        >
                            <b className="result-value">{Math.round(heatloss_design)} kBTU/hr</b>
                        </TextToolTip>
                    </div>
                </div>
                <div className="row">
                    <div className="left-col" align="left">
                        Estimated thermal balance point temperature (TBPT):
                    </div>
                    <div className="right-col">
                        <TextToolTip
                            tooltipText="This is the estimated average outdoor temperature at which the heat pump can no longer meet the home's 
          heating demand on its own, and auxiliary is required. In practice, a home does not have a single, fixed thermal balance point 
          temperature, as heat loss, internal gains, solar gains, thermostat setpoints, and other factors can vary throughout the day—even 
          if the outdoor temperature remains constant. The value provided by ThermalPoint represents a best estimate of the home’s average 
          thermal balance point temperature."
                        >
                            <b className="result-value">
                                {tbpt !== null && tbpt !== undefined && !isNaN(tbpt) ? `${Math.round(tbpt)} °C` : "N/A"}
                            </b>
                        </TextToolTip>
                    </div>
                </div>
                <div className="row">
                    <div className="left-col" align="left">
                        Estimated fraction of the annual heating load that can be met exclusively with the heat pump:
                    </div>
                    <div className="right-col">
                        <TextToolTip
                            tooltipText="This is the estimated percentage of the home's total annual heating demand that can be met exclusively by the heat pump without a need for auxiliary heating. 
            For heat pump systems with gas backup heating, it represents the proportion of heating that can be provided by the heat pump over a heating season. 
            For systems with electric backup heating, the heat pump can continue to operate below the TBPT and the percentage of heating supplied by the heat pump over a heating season will be higher.
            This parameter was calculated using the weather data for the selected location and period."
                        >
                            <b className="result-value">
                                {frac_above_tbpt !== null && frac_above_tbpt !== undefined && !isNaN(frac_above_tbpt)
                                    ? `${Math.round(frac_above_tbpt)} %`
                                    : "N/A"}
                            </b>
                        </TextToolTip>
                    </div>
                </div>
            </div>

            <ResponsiveContainer width={"100%"} height={300}>
                <LineChart data={line_graph_data}>
                    <Tooltip content={CustomToolTip} />
                    <CartesianGrid />
                    <XAxis
                        dataKey="Outdoor Temperature"
                        type="number"
                        label={{
                            value: "Outdoor Temperature [°C]",
                            angle: 0,
                            offset: -3,
                            position: "insideBottom",
                        }}
                        domain={[chartXMin, CHART_X_MAX]}
                        ticks={chartXTicks}
                        allowDataOverflow={true}
                    />
                    <YAxis
                        dataKey={"Heat Loss (kBtu/hr)"}
                        type="number"
                        label={{
                            value: "Heat Loss or HP Capacity [kBTU/hr]",
                            angle: -90,
                            offset: 125,
                            position: "insideBottom",
                        }}
                        domain={[
                            0,
                            Math.ceil(
                                Math.max(
                                    0,
                                    ...(line_graph_data || []).map((d) =>
                                        Math.max(
                                            d["Heat Loss (kBtu/hr)"] || 0,
                                            d["Heat Pump Performance (kBtu/hr)"] || 0,
                                        ),
                                    ),
                                ),
                            ) + 5,
                        ]}
                        allowDataOverflow={true}
                    />
                    <Line type="monotone" dataKey="Heat Loss (kBtu/hr)" stroke="red" />
                    <Line type="monotone" dataKey="Heat Pump Performance (kBtu/hr)" stroke="#00a3af" />
                </LineChart>
            </ResponsiveContainer>

            <hr
                style={{
                    margin: "1.5rem 0",
                    border: "0",
                    borderTop: "1px solid rgba(0, 0, 0, .1)",
                }}
            />
            <section className="results-email-section" aria-labelledby="results-email-heading">
                <h5 id="results-email-heading" className="result-title">
                    Save &amp; share results
                </h5>
                <p className="results-email-section__intro">
                    Enter your project details to receive a copy of these results by email.
                </p>
                <div className="results-email-section__fields">
                    <div className="results-email-section__field">
                        <Input
                            label={<>Project name:</>}
                            type="text"
                            name="project_name"
                            isToolTipFullWidth
                            onChange={(_name, value) => setProjectName(value)}
                            value={projectName}
                        />
                    </div>
                    <div className="results-email-section__field">
                        <Input
                            label={<>Email:</>}
                            tooltipText={<>This email will be used to send the calculation results to you.</>}
                            type="email"
                            name="user_email"
                            isToolTipFullWidth
                            onChange={(_name, value) => setUserEmail(value)}
                            value={userEmail}
                        />
                    </div>
                </div>
                <div className="results-email-section__consent">
                    <Input
                        name="wantsEmailUpdates"
                        checked={wantsEmailUpdates}
                        type="checkbox"
                        onChange={(_name, value) => setWantsEmailUpdates(value)}
                    />
                    <span>I would like to receive emails, newsletters, and exclusive updates on ThermalPoint.</span>
                </div>
                <Button
                    type="button"
                    style={{ width: "fit-content" }}
                    disabled={isEmailSending}
                    onClick={handleEmailResults}
                >
                    {isEmailSending ? "Sending…" : "Email my results"}
                </Button>
                {emailFeedback && (
                    <p
                        className={
                            emailFeedback.type === "error"
                                ? "results-email-section__feedback results-email-section__feedback--error"
                                : "results-email-section__feedback"
                        }
                        role="status"
                    >
                        {emailFeedback.message}
                    </p>
                )}
            </section>

            <hr
                style={{
                    margin: "1.5rem 0",
                    border: "0",
                    borderTop: "1px solid rgba(0, 0, 0, .1)",
                }}
            />
            <h5 className="result-title">Parameter Inputs</h5>
            <div className="parameter-inputs">
                {gas_period_start && gas_period_end && (
                    <ResultRow
                        label="Natural gas billing period:"
                        value={`${formatYmd(gas_period_start)} – ${formatYmd(gas_period_end)}`}
                    />
                )}
                <ResultRow label="Gas Usage Type:" value={dateMode} />

                {dateMode === "Annual" && (
                    <>
                        <ResultRow label="Year:" value={year} />
                        <ResultRow
                            label="Total annual gas use of the home:"
                            tooltipText="Note that ThermalPoint results may be less accurate in homes with few actual gas meter readings throughout the year. The frequency of actual readings may vary by location—many urban and suburban areas typically have actual readings bimonthly, whereas actual readings may be less frequent in rural areas. If there are few Actual readings included in this value, the ThermalPoint results may have greater uncertainty."
                            value={renderGasVolume(gasuse_total, gasuseUnits)}
                            unitToggle={<GasUnitToggle value={gasuseUnits} onChange={setGasuseUnits} />}
                        />
                        <ResultRow
                            label="Estimated annual gas usage for space heating:"
                            value={renderGasVolume(gasuse_heat, gasuseUnits)}
                            unitToggle={<GasUnitToggle value={gasuseUnits} onChange={setGasuseUnits} />}
                        />
                        <ResultRow
                            label="Estimated monthly gas usage for hot water and/or other end uses:"
                            value={renderGasVolume(dhw_monthly, dhwUnits)}
                            unitToggle={<GasUnitToggle value={dhwUnits} onChange={setDhwUnits} />}
                        />
                    </>
                )}

                {dateMode === "Custom" && (
                    <>
                        <ResultRow
                            label="Total gas use of the home over the selected period:"
                            value={renderGasVolume(gasuse_total, gasuseUnits)}
                            unitToggle={<GasUnitToggle value={gasuseUnits} onChange={setGasuseUnits} />}
                        />
                        <ResultRow
                            label="Estimated gas usage for space heating:"
                            value={renderGasVolume(gasuse_heat, gasuseUnits, { decimals: 2 })}
                            unitToggle={<GasUnitToggle value={gasuseUnits} onChange={setGasuseUnits} />}
                        />
                        <ResultRow
                            label="Estimated monthly gas usage for hot water and/or other end uses:"
                            value={renderGasVolume(dhw_monthly, dhwUnits)}
                            unitToggle={<GasUnitToggle value={dhwUnits} onChange={setDhwUnits} />}
                        />
                    </>
                )}

                <ResultRow label="Home Type:" value={home_type ? capitalizeFirstLetter(home_type) : "N/A"} />
                <ResultRow label="Year Built:" value={home_age} />
                <ResultRow
                    label="Energy upgrades since construction:"
                    value={energy_upgrades ? capitalizeFirstLetter(energy_upgrades) : "N/A"}
                />
                <ResultRow
                    label="Typical indoor temperature during the heating season:"
                    value={`${indoor_temp_heat} °C`}
                />
                <ResultRow label="Location:" value={displayLocation} />
                <ResultRow label="Efficiency (AFUE) of existing furnace:" value={`${furnace_eff} %`} />
                <ResultRow
                    label="Assumed outdoor design temperature:"
                    tooltipText="This is the CSA F280 outdoor heating design temperature for your selected location. It represents the expected lowest outdoor temperature based on historical weather data for a specific location. It is not the absolute lowest historical record, but rather a statistically derived typical extreme winter temperature. Contractors and mechanical designers should consult their local standards directly when sizing heat pump systems to ensure compliance."
                    value={`${dtemp} °C`}
                />
                <ResultRow label="Safety margin:" value={`${derate * 100} %`} />
                <ResultRow label="Heat pump capacity:" value={`${heatpumpcap_5} kBTU/hr at 5°F`} />
                <ResultRow indented value={`${heatpumpcap_17} kBTU/hr at 17°F`} />
                <ResultRow indented value={`${heatpumpcap_47} kBTU/hr at 47°F`} />
                <ResultRow
                    fullWidth
                    label={
                        <>
                            <b>Additional Inputs:</b>
                            <ul className="result-row__assumptions">
                                <li>Home is solely heated by natural gas</li>
                                <li>Home is the only building connected to the gas meter</li>
                                <li>
                                    Home may have typical natural gas loads (e.g. furnace, stove, dryer, barbeque,
                                    fireplace, and/or hot water)
                                </li>
                                <li>
                                    There is no pool heater, hot tub, sauna, or comparable non-typical large natural gas
                                    load
                                </li>
                                <li>
                                    Data inputted reflects the current condition of the home (e.g. typical occupancy and
                                    heating behaviour)
                                </li>
                            </ul>
                        </>
                    }
                />
            </div>

            <hr
                style={{
                    margin: "1.5rem 0",
                    border: "0",
                    borderTop: "1px solid rgba(0, 0, 0, .1)",
                }}
            />
            <h6 className="result-title">Terms and Conditions of Use</h6>
            {/* <p style={{ fontSize: "smaller" }}>
        Refer to the <a href="/terms-and-conditions" style={{ textDecoration: 'underline', color: 'blue' }}>Terms and Conditions</a> for further information on Disclaimer of Liability, Limitations of Applicability, and additional disclaimers, regarding the results provided by this application.
      </p> */}

            <p style={{ fontSize: "12px", maxWidth: "850px" }}>
                The calculations provided on this webpage estimate the thermal balance point and potential heat pump
                utilization by analyzing historical gas consumption data.
                <b>
                    The calculations involve an estimate of the average heat loss of the home relative to outdoor
                    temperatures. This is not the same as calculating the design heat loss of the home. Design heat loss
                    must be determined in accordance with the CSA F280-12 standard.
                </b>{" "}
                Consequently, while the calculations may offer some insight into backup or supplemental heating system
                sizing, they do not provide sufficient information to be used as the sole basis for backup or
                supplemental system sizing in outdoor design conditions. Users should independently verify all results
                and should not rely solely on the calculations for critical decision-making without professional
                consultation.
                <br></br>
                <br></br>
                The calculations on this webpage are for informational purposes only and are intended to offer general
                guidance. While we strive to ensure the accuracy of the calculations, we do not guarantee the results or
                their applicability to specific homes or situations. The responsibility for correctly sizing and
                selecting heating and cooling equipment lies solely with the HVAC contractor or mechanical designer. The
                results generated by this tool should be independently verified by a qualified professional. Toronto and
                Region Conservation Authority (“TRCA”) assumes no liability for any direct or indirect consequences
                arising from the use or reliance on the information provided by this tool. This software is provided in
                an “AS IS” basis. TRCA shall not be liable for loss or damage arising out of the installation, use or
                uninstallation of the software. TRCA disclaims all warranties, either express or implied. TRCA makes no
                warranties, expressed or implied, and hereby disclaims and negates all other warranties, including
                without limitation, implied warranties or conditions of merchantability, fitness for a particular
                purpose, or non-infringement of intellectual property or other violation of rights. Further, TRCA does
                not warrant or make any representations concerning the accuracy, likely results, or reliability of the
                use of the tool or otherwise relating to such materials or on any sites linked to this tool.
                <br></br>
                <br></br>
                In no event shall TRCA be liable for any damages (including, without limitation, damages for loss of
                data or profit, or due to business interruption), arising out of the use or inability to use this tool,
                even if TRCA or a TRCA authorized representative has been notified orally or in writing of the
                possibility of such damage. Because some jurisdictions do not allow limitations on implied warranties,
                or limitations of liability for consequential or incidental damages, these limitations may not apply to
                you.
                <br></br>
                <br></br>
                Any use of trade or product names does not imply endorsement or recommendation of those products. This
                work is protected under Canadian copyright law and other international copyright treaties and
                conventions. No part of this work may be reproduced or transmitted for commercial purposes, in any form
                or by any means, electronic or mechanical, including photocopying and recording, or by any information
                storage or retrieval system, except as expressly permitted in writing by Sustainable Technologies
                Evaluation Program (STEP).
                <br></br>
                <br></br>
                Toronto and Region Conservation Authority may revise these terms of use for the tool at any time without
                notice. By using this tool, you agree to be bound by these Terms and Conditions of Use.
            </p>
        </PageLayout>
    );
};

export default Result;
